import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy = ({ onClose }) => {
  const { t } = useTranslation();
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
          <h1 className="text-2xl font-bold">{t('legal.privacy.title')}</h1>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6 text-gray-700">
          <p className="text-sm text-gray-500">
            <strong>Last Updated:</strong> February 1, 2025
          </p>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. Introduction</h2>
            <p className="leading-relaxed">
              Magic Moments ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains 
              how we collect, use, disclose, and safeguard your information when you use our service ("Service"). 
              Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, 
              please do not access the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2.1 Personal Information</h3>
            <p className="mb-2">We collect personal information that you voluntarily provide when using our Service:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong>Account Information:</strong> Name, email address, password</li>
              <li><strong>Payment Information:</strong> Credit card details (processed by third-party payment processors)</li>
              <li><strong>Profile Information:</strong> Optional profile photo and preferences</li>
              <li><strong>Contact Information:</strong> If you contact our support team</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2.2 User Content</h3>
            <p className="mb-2">Content you upload to our Service:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong>Photos and Videos:</strong> Images and videos you upload</li>
              <li><strong>Albums:</strong> Album names and organization</li>
              <li><strong>Generated Content:</strong> AI-generated animated moments</li>
              <li><strong>Metadata:</strong> EXIF data, timestamps, and file information</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2.3 Automatically Collected Information</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent</li>
              <li><strong>Log Data:</strong> Server logs, error reports, performance data</li>
              <li><strong>Cookies:</strong> Session cookies, preference cookies, analytics cookies</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2.4 Third-Party Information</h3>
            <p className="leading-relaxed">
              If you connect your account with third-party services (social media, cloud storage), 
              we may receive information from those services in accordance with their terms and your privacy settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <p className="mb-2">We use collected information for the following purposes:</p>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">3.1 Service Provision</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Create and manage your account</li>
              <li>Process and store your uploaded content</li>
              <li>Generate AI-powered animated moments</li>
              <li>Enable album sharing and collaboration features</li>
              <li>Process payments and manage credits</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">3.2 Service Improvement</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Analyze usage patterns to improve features</li>
              <li>Develop new features and services</li>
              <li>Test and debug technical issues</li>
              <li>Optimize performance and user experience</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">3.3 Communication</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Send service-related notifications</li>
              <li>Respond to support inquiries</li>
              <li>Provide updates about features or changes</li>
              <li>Send marketing communications (with consent)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">3.4 Security and Legal</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Detect and prevent fraud or abuse</li>
              <li>Enforce our Terms of Service</li>
              <li>Comply with legal obligations</li>
              <li>Protect rights and safety of users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. AI Processing and Data Usage</h2>
            <div className="space-y-2">
              <p><strong>4.1 AI Analysis:</strong> Your uploaded images are processed by artificial intelligence systems to generate animated content and analyze visual features.</p>
              <p><strong>4.2 No Training:</strong> Your personal content is NOT used to train our AI models unless you explicitly opt-in to a training program.</p>
              <p><strong>4.3 Processing Location:</strong> AI processing may occur on servers in various locations to optimize performance.</p>
              <p><strong>4.4 Data Retention:</strong> Original and processed content is retained as long as your account is active.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. Information Sharing and Disclosure</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">5.1 With Your Consent</h3>
            <p className="leading-relaxed">
              We share information when you explicitly authorize us to do so, such as when sharing albums with collaborators.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">5.2 Service Providers</h3>
            <p className="mb-2">We share information with third-party service providers who assist us:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li><strong>Media Storage & Delivery:</strong> Cloudflare Images and Cloudflare Stream for secure image and video storage, optimization, and delivery with encrypted transmission</li>
              <li><strong>Database Hosting:</strong> MongoDB Atlas for encrypted data storage with enterprise-grade security</li>
              <li><strong>Payment Processing:</strong> Stripe for secure payment processing (we do not store credit card details)</li>
              <li><strong>AI Services:</strong> Third-party AI providers for image analysis and video generation</li>
              <li><strong>Email Services:</strong> SendGrid for transactional email delivery</li>
              <li><strong>Analytics:</strong> Usage analytics and monitoring services for service improvement</li>
            </ul>
            <p className="mt-2 text-sm italic">All service providers are contractually bound to protect your data and may only use it for the specific services they provide to us.</p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">5.3 Legal Requirements</h3>
            <p className="mb-2">We may disclose information if required by law or in good faith belief that such action is necessary to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Comply with legal obligations or court orders</li>
              <li>Protect and defend our rights or property</li>
              <li>Prevent fraud or investigate violations</li>
              <li>Protect the safety of users or the public</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">5.4 Business Transfers</h3>
            <p className="leading-relaxed">
              If we are involved in a merger, acquisition, or sale of assets, your information may be transferred. 
              We will provide notice before your information becomes subject to a different privacy policy.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">5.5 Aggregated Data</h3>
            <p className="leading-relaxed">
              We may share aggregated, non-identifiable data for research, marketing, or analytics purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. Data Security</h2>
            <div className="space-y-2">
              <p><strong>6.1 Multi-Layer Security:</strong> We implement enterprise-grade security measures including:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Encryption in Transit:</strong> All data transmission uses TLS 1.3 encryption</li>
                <li><strong>Encryption at Rest:</strong> All stored data is encrypted using AES-256 encryption</li>
                <li><strong>Cloudflare Protection:</strong> DDoS protection, WAF (Web Application Firewall), and bot mitigation</li>
                <li><strong>Signed URLs:</strong> All media access uses time-limited, cryptographically signed URLs</li>
                <li><strong>Access Controls:</strong> Role-based access control and multi-factor authentication for administrative access</li>
              </ul>
              <p className="mt-2"><strong>6.2 Secure Infrastructure:</strong> Our infrastructure leverages Cloudflare's global network with:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Automatic HTTPS enforcement</li>
                <li>ISO 27001 and SOC 2 Type II certified data centers</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Geographic redundancy and automatic failover</li>
              </ul>
              <p className="mt-2"><strong>6.3 Access Restrictions:</strong> Only authorized personnel have access to user data, subject to strict confidentiality agreements and background checks.</p>
              <p><strong>6.4 Security Monitoring:</strong> 24/7 automated monitoring for suspicious activity, with immediate alerting and response protocols.</p>
              <p><strong>6.5 No Guarantee:</strong> While we implement industry-leading security measures, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.</p>
              <p><strong>6.6 Breach Notification:</strong> In the event of a data breach affecting your personal information, we will notify you within 72 hours as required by law, including details about the breach and remediation steps.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. Data Storage and Retention</h2>
            <div className="space-y-2">
              <p><strong>7.1 Storage Infrastructure:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Images:</strong> Stored on Cloudflare Images with automatic optimization and global CDN delivery</li>
                <li><strong>Videos:</strong> Stored on Cloudflare Stream with adaptive streaming and thumbnail generation</li>
                <li><strong>User Data:</strong> Stored in MongoDB Atlas with automated backups and encryption</li>
                <li><strong>Geographic Distribution:</strong> Data is replicated across multiple data centers for redundancy</li>
              </ul>
              <p className="mt-2"><strong>7.2 Active Accounts:</strong> We retain your information as long as your account is active or as needed to provide services.</p>
              <p><strong>7.3 Deleted Accounts:</strong> After account deletion:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>User data and content are permanently deleted from our active systems within 30 days</li>
                <li>Media files are removed from Cloudflare Images and Stream</li>
                <li>Database records are purged from MongoDB Atlas</li>
                <li>Backup data may persist for up to 90 days before automatic expiration</li>
              </ul>
              <p><strong>7.4 Legal Requirements:</strong> We may retain certain information longer if required by law, for fraud prevention, or to resolve disputes.</p>
              <p><strong>7.5 Inactive Accounts:</strong> Accounts inactive for 24+ months may be deleted with 30 days advance notice.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Your Privacy Rights</h2>
            <p className="mb-2">Depending on your location, you may have the following rights:</p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">8.1 Access and Portability</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Request a copy of your personal data</li>
              <li>Download your uploaded content</li>
              <li>Receive data in a structured, machine-readable format</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">8.2 Correction and Updates</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Correct inaccurate personal information</li>
              <li>Update your account details</li>
              <li>Modify privacy preferences</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">8.3 Deletion</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Delete your account and associated data</li>
              <li>Request removal of specific content</li>
              <li>Erasure of personal information (subject to legal exceptions)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">8.4 Restriction and Objection</h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Restrict processing of your data</li>
              <li>Object to certain data uses</li>
              <li>Opt-out of marketing communications</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">8.5 Exercising Rights</h3>
            <p className="leading-relaxed">
              To exercise these rights, contact us at privacy@magicmoments.com. We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">9. Cookies and Tracking Technologies</h2>
            <div className="space-y-2">
              <p><strong>9.1 Cookie Usage:</strong> We use cookies and similar technologies to enhance your experience.</p>
              <p><strong>9.2 Types of Cookies:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Essential:</strong> Required for authentication and security</li>
                <li><strong>Functional:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics:</strong> Understand how you use our Service</li>
                <li><strong>Marketing:</strong> Deliver relevant advertisements (with consent)</li>
              </ul>
              <p><strong>9.3 Cookie Control:</strong> You can control cookies through your browser settings. Disabling cookies may limit functionality.</p>
              <p><strong>9.4 Do Not Track:</strong> We honor Do Not Track signals where technically feasible.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">10. Children's Privacy</h2>
            <div className="space-y-2">
              <p><strong>10.1 Age Restriction:</strong> Our Service is not intended for children under 13 years of age.</p>
              <p><strong>10.2 No Collection:</strong> We do not knowingly collect personal information from children under 13.</p>
              <p><strong>10.3 Parental Rights:</strong> If you believe we have collected information from a child under 13, contact us immediately for deletion.</p>
              <p><strong>10.4 Teen Users:</strong> Users aged 13-18 should have parental consent to use the Service.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">11. International Data Transfers</h2>
            <div className="space-y-2">
              <p><strong>11.1 Global Service:</strong> Your information may be transferred to and processed in countries other than your own.</p>
              <p><strong>11.2 Data Protection:</strong> We ensure appropriate safeguards are in place for international transfers.</p>
              <p><strong>11.3 EU-US Transfers:</strong> We comply with applicable data transfer frameworks for EU user data.</p>
              <p><strong>11.4 Consent:</strong> By using our Service, you consent to international data transfers.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">12. California Privacy Rights (CCPA)</h2>
            <p className="mb-2">If you are a California resident, you have additional rights:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to opt-out of sale of personal information</li>
              <li>Right to deletion of personal information</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
            <p className="mt-2"><strong>Note:</strong> We do not sell your personal information.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">13. European Privacy Rights (GDPR)</h2>
            <p className="mb-2">If you are in the European Economic Area (EEA), you have rights under GDPR including:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">14. Data Storage and Third-Party Services</h2>
            <p className="leading-relaxed">
              We use Cloudflare R2 for backend storage and MongoDB for managing user data. By using our services, you acknowledge that your data (including any content you upload) may be stored and processed through these third-party platforms. We take reasonable measures to ensure these services comply with industry standards for data protection.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">15. Administrative Access to User Content</h2>
            <div className="space-y-2">
              <p className="leading-relaxed">
                As operators of the Magic Moments platform, our authorized personnel have technical access to backend storage systems, including user-uploaded photos and videos. This access is necessary for:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Technical support and troubleshooting</li>
                <li>System maintenance and optimization</li>
                <li>Content moderation and compliance</li>
                <li>Security monitoring and abuse prevention</li>
              </ul>
              <p className="mt-2 leading-relaxed">
                <strong>Content Moderation:</strong> We reserve the right to review user content to ensure compliance with our Terms of Service and applicable laws. Prohibited content includes, but is not limited to, illegal material, pornography, hate speech, and content that violates intellectual property rights.
              </p>
              <p className="mt-2 leading-relaxed">
                <strong>Law Enforcement Cooperation:</strong> We will cooperate with law enforcement agencies and comply with valid legal requests, including subpoenas, court orders, and search warrants. We may disclose user information and content when legally required or when we believe in good faith that such disclosure is necessary to:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Comply with legal obligations or processes</li>
                <li>Investigate or prevent illegal activities</li>
                <li>Protect the rights, property, or safety of Magic Moments, our users, or the public</li>
                <li>Enforce our Terms of Service</li>
              </ul>
              <p className="mt-2 leading-relaxed">
                Access to user data is restricted to authorized personnel only, subject to strict confidentiality agreements, and is logged for security purposes.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">16. Account Suspension or Termination</h2>
            <div className="space-y-2">
              <p className="leading-relaxed">
                We reserve the right to suspend or terminate your account if we find that you have violated our Terms of Service, including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Uploading prohibited or illegal content</li>
                <li>Engaging in fraudulent or abusive behavior</li>
                <li>Violating intellectual property rights</li>
                <li>Attempting to compromise system security</li>
                <li>Using the service for unauthorized commercial purposes</li>
              </ul>
              <p className="mt-2 leading-relaxed">
                <strong>Notification:</strong> We will notify you of any suspension or termination actions and, where appropriate, provide a reason for the action. You may appeal account termination decisions by contacting our support team.
              </p>
              <p className="mt-2 leading-relaxed">
                <strong>Data Retention After Termination:</strong> Upon account termination, your data will be handled according to our standard deletion procedures outlined in Section 7. However, we may retain certain information longer if required by law or for legitimate business purposes such as fraud prevention or dispute resolution.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">17. User Rights and Contact Information</h2>
            <div className="space-y-2">
              <p className="leading-relaxed">
                You have the right to access, correct, or delete your personal data stored with us. To exercise these rights or if you have any questions or concerns about your privacy, you can contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mt-2">
                <p><strong>Email:</strong> privacy@magicmoments.com</p>
                <p className="mt-2"><strong>Support Email:</strong> support@magic-moments.ai</p>
              </div>
              <p className="mt-2 leading-relaxed">
                We will respond to your requests within 30 days and will take reasonable steps to verify your identity before processing your request.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">18. Changes to Privacy Policy</h2>
            <div className="space-y-2">
              <p><strong>18.1 Updates:</strong> We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements.</p>
              <p><strong>18.2 Notification:</strong> We will notify you of material changes via email or Service notification at least 30 days before changes take effect.</p>
              <p><strong>18.3 Review:</strong> We encourage you to review this Privacy Policy regularly.</p>
              <p><strong>18.4 Continued Use:</strong> Continued use after changes constitutes acceptance of the updated Privacy Policy.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">19. Third-Party Links</h2>
            <p className="leading-relaxed">
              Our Service may contain links to third-party websites. We are not responsible for the privacy practices 
              of these external sites. We encourage you to read their privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">20. Contact Us</h2>
            <p className="leading-relaxed mb-2">
              If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-2">
              <p><strong>Email:</strong> support@magic-moments.ai</p>
            </div>
          </section>

          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-sm text-gray-500 italic">
              By using Magic Moments, you acknowledge that you have read and understood this Privacy Policy 
              and consent to the collection, use, and disclosure of your information as described herein.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 p-4 rounded-b-2xl border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PrivacyPolicy;
